import React, { useState } from 'react';
import HarbourModal from './HarbourModal';
import HarbourNotice from './HarbourNotice';
import HarbourTextLink from './HarbourTextLink';
import { HarbourField } from './HarbourField';
import { apiHarbourUpdateSchoolTeacher, apiHarbourSetSchoolTeacherStatus } from '../../utils/rmsApi';
import { TEACHER_ROLE_MAX_LENGTH, newTeacherErrors, teacherFullName } from '../../utils/schoolTeachers';
import './harbour.css';

/*
 * "Change teacher" (TDI-617, design 4153:286473, built as the ET-1 details
 * modal): the teacher's details filled in, with two ways out, taking them
 * off this pupil and marking that they no longer work at the school.
 *
 * Name, role and phone save at once. A changed email asks first, then moves
 * the school's record to the new address and emails both. "No longer works
 * at {school}" asks first too. Forms are given from "{name}'s forms", not
 * here.
 *
 * onSaved(teacher) after a save; onRemove() opens the existing "Remove
 * {name}?" confirm on the record; onLeft({ teacher, unfinishedCount }) after
 * the leaver is marked.
 */
export default function HarbourChangeTeacherModal({
  orgId, pupil, teacher, schoolName, onClose, onSaved, onRemove, onLeft,
}) {
  const initial = {
    firstName: teacher.firstName || '',
    lastName: teacher.lastName || '',
    email: teacher.email || '',
    jobRole: teacher.jobRole || '',
    phone: teacher.phone || '',
  };
  const [form, setForm] = useState(initial);
  const [step, setStep] = useState('details'); // 'details' | 'confirmEmail' | 'confirmLeave'
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState(null);
  const [duplicate, setDuplicate] = useState(null);

  const first = teacher.firstName || 'this teacher';
  const name = teacherFullName(teacher) || teacher.email || 'this teacher';
  const childFirst = (pupil.name || '').split(' ')[0];
  const school = schoolName || 'your school';
  const errors = submitted ? newTeacherErrors(form) : {};
  const newEmail = form.email.trim();
  const emailChanged = newEmail.toLowerCase() !== String(initial.email).trim().toLowerCase();

  const setField = (field) => (e) => {
    const { value } = e.target;
    setForm((f) => ({ ...f, [field]: value }));
    if (field === 'email') setDuplicate(null);
  };

  const send = async (confirmEmailChange) => {
    setBusy(true);
    setFailure(null);
    try {
      const res = await apiHarbourUpdateSchoolTeacher({
        orgId,
        patientRecordId: pupil.id,
        schoolTeacherId: teacher.schoolTeacherId,
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        email: newEmail,
        jobRole: form.jobRole.trim(),
        phone: form.phone.trim() || null,
        confirmEmailChange,
      });
      if (res && res.ok === false && res.code === 'ALREADY_ON_SCHOOL_LIST') {
        setDuplicate(res.teacher || {});
        setStep('details');
        return;
      }
      onSaved((res && res.teacher) || {});
    } catch (e) {
      setStep('details');
      setFailure(e?.message || "The teacher's details could not be saved. Check your connection and try again.");
    } finally {
      setBusy(false);
    }
  };

  const save = () => {
    setSubmitted(true);
    setDuplicate(null);
    if (Object.keys(newTeacherErrors(form)).length) return;
    if (emailChanged) {
      setStep('confirmEmail');
      return;
    }
    send(false);
  };

  const markLeft = async () => {
    setBusy(true);
    setFailure(null);
    try {
      const res = await apiHarbourSetSchoolTeacherStatus({
        orgId, patientRecordId: pupil.id, schoolTeacherId: teacher.schoolTeacherId, status: 'left',
      });
      onLeft(res || {});
    } catch (e) {
      setStep('details');
      setFailure(e?.message || 'That did not work. Check your connection and try again.');
    } finally {
      setBusy(false);
    }
  };

  if (step === 'confirmEmail') {
    return (
      <HarbourModal
        title={`Change ${first}'s email address?`}
        confirmLabel="Change email address"
        busy={busy}
        onConfirm={() => send(true)}
        onClose={() => setStep('details')}
      >
        <p className="harbour-body-16">
          {`They'll sign in with ${newEmail} from now on. We'll email both addresses to let them know. Any sign-in links already sent to ${initial.email} will stop working.`}
        </p>
      </HarbourModal>
    );
  }

  if (step === 'confirmLeave') {
    return (
      <HarbourModal
        title={`${name} no longer works at ${school}?`}
        confirmLabel="Yes, they've left"
        busy={busy}
        onConfirm={markLeft}
        onClose={() => setStep('details')}
      >
        <p className="harbour-body-16">
          {`They won't be able to sign in for any of ${school}'s pupils. Forms they haven't finished come back to you, with their answers so far, so you can give them to someone else. Forms they finished stay as they are.`}
        </p>
      </HarbourModal>
    );
  }

  const eyebrow = [pupil.name, pupil.yearGroup].filter(Boolean).join(' ');
  const duplicateName = duplicate ? (teacherFullName(duplicate) || duplicate.email || newEmail) : null;
  return (
    <HarbourModal
      title="Change teacher"
      eyebrow={eyebrow}
      confirmLabel="Save"
      busy={busy}
      onConfirm={save}
      onClose={onClose}
    >
      <div className="flex flex-col gap-4 text-left">
        <HarbourField
          label="First name"
          value={form.firstName}
          autoComplete="off"
          error={errors.firstName}
          onChange={setField('firstName')}
        />
        <HarbourField
          label="Last name"
          value={form.lastName}
          autoComplete="off"
          error={errors.lastName}
          onChange={setField('lastName')}
        />
        <HarbourField
          label="Email address"
          hint="We send their sign-in email here. Use their school email if they have one."
          type="email"
          inputMode="email"
          autoCapitalize="none"
          spellCheck={false}
          autoComplete="off"
          value={form.email}
          error={errors.email}
          onChange={setField('email')}
        />
        <HarbourField
          label="Role"
          hint="For example, Year 3 class teacher or maths teacher"
          maxLength={TEACHER_ROLE_MAX_LENGTH}
          autoComplete="off"
          value={form.jobRole}
          error={errors.jobRole}
          onChange={setField('jobRole')}
        />
        <HarbourField
          label="Phone number"
          hint="Optional"
          type="tel"
          autoComplete="off"
          value={form.phone}
          onChange={setField('phone')}
        />
        {duplicate && (
          <HarbourNotice kind="warning" title={`${duplicateName} is already on your school's list`} />
        )}
        {failure && (
          <HarbourNotice kind="error" title="That did not work">{failure}</HarbourNotice>
        )}
        <div className="flex flex-col items-start gap-2">
          <HarbourTextLink disabled={busy} onClick={onRemove}>
            {`Remove ${first} from ${childFirst}'s record`}
          </HarbourTextLink>
          <HarbourTextLink disabled={busy} onClick={() => { setFailure(null); setStep('confirmLeave'); }}>
            {`${first} no longer works at ${school}`}
          </HarbourTextLink>
        </div>
      </div>
    </HarbourModal>
  );
}
